/**
 * CartEditModal - Handles cart item editing functionality
 * Allows customers to edit cart items by changing variants, sizes, and colors
 */
class CartEditModal {
  constructor() {
    this.modal = document.querySelector('[data-cart-edit-modal]');
    this.modalContent = this.modal?.querySelector('[data-modal-content]');
    this.modalOverlay = this.modal?.querySelector('[data-modal-overlay]');
    this.modalClose = this.modal?.querySelector('[data-modal-close]');
    this.loader = this.modal?.querySelector('.cart__loader');
    
    this.originalCartItem = null;
    this.cartLineIndex = null;
    
    this.init();
  }

  init() {
    if (!this.modal) return;

    document.addEventListener('click', this.handleEditClick.bind(this));
    this.modalClose?.addEventListener('click', this.closeModal.bind(this));
    this.modalOverlay?.addEventListener('click', this.closeModal.bind(this));
    document.addEventListener('cart-edit:swatch-change', this.handleSwatchChange.bind(this));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });
  }

  handleEdit(productHandle) {
    if (this.originalCartItem) {
      this.originalCartItem.productHandle = productHandle;
    }
    if (this.modalContent) {
      this.modalContent.innerHTML = '';
    }
    if (this.loader) {
      this.loader.style.display = 'block';
    }
    this.loadProductData(productHandle);
  }

  handleSwatchChange(event) {
    const { productHandle } = event.detail;
    this.handleEdit(productHandle);
  }

  handleEditClick(event) {
    const editButton = event.target.closest('.cart__edit');
    if (!editButton) return;

    event.preventDefault();    
    const cartItem = editButton.closest('[data-cart-item]');
    if (!cartItem) {
      console.error('Cart item container not found');
      return;
    }
    const productHandle = cartItem.getAttribute('data-product-handle');
    const variantId = cartItem.getAttribute('data-variant-id');
    const lineIndex = cartItem.getAttribute('data-line-index');
    const quantity = cartItem.getAttribute('data-product-quantity');
    
    if (!productHandle || !lineIndex) {
      console.error('Product handle or line index not found');
      return;
    }

    this.originalCartItem = {
      productHandle: productHandle,
      variantId: variantId,
      quantity: parseInt(quantity) || 1,
      lineIndex: parseInt(lineIndex)
    };
    this.cartLineIndex = parseInt(lineIndex);

    this.openModal();
    this.loadProductData(productHandle, variantId);
    document.dispatchEvent(new CustomEvent('close:cart:drawer'));
  }

  openModal() {
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.modal}));
    
    if (this.loader) {
      this.loader.style.display = 'block';
    }

    setTimeout(() => {
      const firstFocusable = this.modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 600);
  }

  closeModal() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    document.dispatchEvent(new CustomEvent('theme:tooltip:close', {
      bubbles: false,
      detail: { hideTransition: false },
    }));
    
    if (this.simpleSlider) {
      try {
        this.simpleSlider = null;
      } catch (error) {
        console.error('CartEditModal: Error cleaning up simple slider:', error);
      }
    }
    
    this.prevButton = null;
    this.nextButton = null;
    
    if (this.productForm && typeof this.productForm.destroy === 'function') {
      try {
        this.productForm.destroy();
      } catch (error) {
        console.error('CartEditModal: Error destroying ProductForm:', error);
      }
      this.productForm = null;
    }

    if (this.productAddForm && typeof this.productAddForm.destroy === 'function') {
      try {
        this.productAddForm.destroy();
      } catch (error) {
        console.error('CartEditModal: Error destroying ProductAddForm:', error);
      }
      this.productAddForm = null;
    }

    if (this.selloutVariants) {
      this.selloutVariants = null;
    }
    
    this.originalCartItem = null;
    this.cartLineIndex = null;
    
    if (this.modalContent) {
      this.modalContent.innerHTML = '';
    }
  }

  async loadProductData(productHandle, variantId = null) {
    try {
      let url = `${window.theme.routes.root}products/${productHandle}?section_id=api-product-upsell`;
      if (variantId) {
        url += `&variant=${variantId}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      
      if (this.loader) {
        this.loader.style.display = 'none';
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const apiContent = doc.querySelector('[data-api-content]');
      
      if (apiContent && this.modalContent) {
        this.modalContent.innerHTML = apiContent.innerHTML;
          this.initFormFunctionalities();
      } else {
        console.error('API content not found in response');
        throw new Error('Invalid response format');
      }

    } catch (error) {
      console.error('Error loading product data:', error);
      if (this.loader) {
        this.loader.style.display = 'none';
      }
      
      if (this.modalContent) {
        this.modalContent.innerHTML = '<div style="padding: 20px; text-align: center;"><p>Error loading product information. Please try again.</p></div>';
      }
    }
  }

  initFormFunctionalities() {
    const slidesWithAOS = this.modalContent.querySelectorAll('[data-animation]');
    slidesWithAOS.forEach(element => {
      element.removeAttribute('data-animation');
      element.removeAttribute('data-animation-duration');
      element.removeAttribute('data-animation-delay');
      element.classList.remove('aos-initialized');
      element.style.removeProperty('opacity');
      element.style.removeProperty('transform');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('animation-delay');
    });

    const addToCartButtons = this.modalContent.querySelectorAll('[data-add-to-cart]');
    addToCartButtons.forEach(button => {
      button.querySelector('[data-add-to-cart-text]').textContent = theme.strings.ChooseaSize;
      button.querySelector('.desktop_selector_addButton').style.display = 'none';
      button.setAttribute('data-add-text', 'Update Cart');
      if (!button.getAttribute('data-original-text')) {
        button.setAttribute('data-original-text', button.querySelector('[data-add-to-cart-text]').textContent);
      }
      button.addEventListener('click', (e) => {
        this.onFormSubmit(e);
        // this.closeModal();
      });
    });
    this.initProductForm();
    setTimeout(() => {
      if (!this.productForm || !this.modalContent) return;
      const productJsonEl = this.modalContent.querySelector('[data-product-json]');
      if (!productJsonEl) return;
      try {
        const productData = JSON.parse(productJsonEl.innerHTML);
        const selectedOptions = productData.options.map((_, index) => {
          const checked = this.modalContent.querySelector(
            `[data-single-option-selector][data-index="option${index + 1}"]:checked`
          );
          return checked ? checked.value : null;
        });
        const allSelected = selectedOptions.every(v => v !== null);
        const variant = allSelected
          ? (productData.variants.find(v => selectedOptions.every((val, i) => v.options[i] === val)) || null)
          : null;
        this.updateProductState({ variant });
      } catch (e) {
        this.updateProductState(this.productForm.getFormState());
      }
    }, 200);
    this.initSwatches();
    this.initTooltips();
    this.initPopouts();
    this.initSizeSelector();
    const form = this.modalContent.querySelector('form');
    if (form) {
      form.classList.add('cart-edit-modal__form');
    }
    this.initSlider();
  }

  initProductForm() {
    const productJson = this.modalContent.querySelector('[data-product-json]');
    const productForm = this.modalContent.querySelector('form');
    
    if (!productJson || !productForm) {
      console.error('CartEditModal: Missing product JSON or form element');
      return;
    }
    try {
      const productData = JSON.parse(productJson.innerHTML);
      this.productForm = new ProductForm(productForm, productData, {
        onOptionChange: this.onOptionChange.bind(this),
        onPlanChange: this.onPlanChange.bind(this),
        onFormSubmit: this.onFormSubmit.bind(this)
      });
      const initialFormState = this.productForm.getFormState();
      this.updatePrices(initialFormState.variant);
    } catch (error) {
      console.error('CartEditModal: Error initializing ProductForm:', error);
    }
  }

  onOptionChange(evt) {
    this.updateProductState(evt.dataset);
    this.updateProductImage(evt);
  }

  onPlanChange(evt) {
    this.updateProductState(evt.dataset);
  }
  onFormSubmit(evt) {
    if (evt && evt.preventDefault) {
      evt.preventDefault();
    }
    if (!this.originalCartItem || !this.cartLineIndex) {
      return;
    }
    const variantInput = this.modalContent.querySelector('input[name="id"]');
    const newVariantId = variantInput?.value;
    const quantity = this.originalCartItem.quantity;
    const sizeInput = this.modalContent.querySelector('input#properties-size-input');
    const size = sizeInput ? sizeInput.value : '';
    if (!newVariantId) {
      return;
    }
    if (newVariantId.toString() === this.originalCartItem.variantId.toString()) {
      this.closeModal();
      return;
    }
    const addToCartButtons = this.modalContent.querySelectorAll('[data-add-to-cart]');
    addToCartButtons.forEach(button => {
      button.disabled = true;
      button.classList.add('loading');
      button.textContent = 'Updating...';
    });
    this.updateCartItem(newVariantId, quantity, size);
  }

  async updateCartItem(newVariantId, quantity, size) {
    try {
      const removeData = {
        line: this.cartLineIndex,
        quantity: 0
      };      
      const removeResponse = await fetch(theme.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(removeData)
      });
      if (!removeResponse.ok) {
        throw new Error('Failed to remove old cart item');
      }
      const addData = {
        id: newVariantId,
        quantity: quantity,
        properties: {
          _Size: size
        }
      };
      const addResponse = await fetch(theme.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(addData)
      });
      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Failed to add new variant to cart');
      }
      const addResult = await addResponse.json();
      document.dispatchEvent(new CustomEvent('theme:product:add', { bubbles: true }));
      this.closeModal();
      document.dispatchEvent(new CustomEvent('theme:cart:item-updated', {
        detail: {
          oldVariantId: this.originalCartItem.variantId,
          newVariantId: newVariantId,
          quantity: quantity
        }
      }));
    } catch (error) {
      const addToCartButtons = this.modalContent.querySelectorAll('[data-add-to-cart]');
      addToCartButtons.forEach(button => {
        button.disabled = false;
        button.classList.remove('loading');
        button.textContent = '';
      });
    }
  }

  extractCurrentOptions() {
    const options = [];
    
    const productJson = this.modalContent.querySelector('[data-product-json]');
    if (!productJson) return options;
    
    try {
      const product = JSON.parse(productJson.innerHTML);
      product.options.forEach((optionName, index) => {
      let selectedValue = null;
                
        const optionInputs = this.modalContent.querySelectorAll(`input[data-index="option${index + 1}"]`);
        
        optionInputs.forEach(input => {
          if (input.checked) {
            selectedValue = input.value;
          }
        });
        
        if (selectedValue) {
          options.push({
            name: optionName,
            value: selectedValue
          });
        }
      });
      return options;
    } catch (error) {
      console.error('Error extracting current options:', error);
      return options;
    }
  }

  updateProductState(formState) {
    const variant = formState.variant;
    const addToCartButtons = this.modalContent.querySelectorAll('[data-add-to-cart]');

    if (!variant) {
      addToCartButtons.forEach(button => {
        button.disabled = true;
        const textEl = button.querySelector('[data-add-to-cart-text]');
        if (textEl) textEl.textContent = 'Unavailable';
      });
      this.updateVariantOptions(formState);
      if (this.selloutVariants) this.selloutVariants.update();
      return;
    }

    addToCartButtons.forEach(button => {
      if (variant.available) {
        button.disabled = false;
        button.querySelector('[data-add-to-cart-text]').textContent = '';
        button.querySelector('.desktop_selector_addButton').style.display = 'none';
        button.setAttribute('data-add-text', 'Update Cart');
      } else {
        button.disabled = true;
        button.querySelector('[data-add-to-cart-text]').textContent = 'Unavailable';
      }
    });

    const variantInput = this.modalContent.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;
    this.updatePrices(variant);
    this.updateVariantOptions(formState);
    if (this.selloutVariants) this.selloutVariants.update();
  }

  updatePrices(variant) {
    const priceElements = this.modalContent.querySelectorAll('[data-product-price]');
    const comparePriceElements = this.modalContent.querySelectorAll('[data-compare-price]');
    priceElements.forEach(el => {
      if (variant.price) {
        el.textContent = themeVendor.themeCurrency.formatMoney(variant.price, theme.moneyFormat);
      }
    });
    const priceOffEl = this.modalContent.querySelector('[data-price-off]');
    const priceOffAmountEl = this.modalContent.querySelector('[data-price-off-amount]');
    comparePriceElements.forEach(el => {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        el.textContent = themeVendor.themeCurrency.formatMoney(variant.compare_at_price, theme.moneyFormat);
        el.style.display = '';
        if (priceOffEl) priceOffEl.classList.remove("hide");
        if (priceOffAmountEl) priceOffAmountEl.innerHTML = (100 * (variant.compare_at_price - variant.price)/variant.compare_at_price).toFixed(0) + "%";
      } else {
        if (priceOffEl) priceOffEl.classList.add("hide");
        el.style.display = 'none';
      }
    });
  }

  updateVariantOptions(formState) {
    const optionValues = this.modalContent.querySelectorAll('[data-option-value]');
    const variant = formState.variant;
    const propertiesSizeLabel = this.modalContent.querySelector('.product-variant-name');
    if (propertiesSizeLabel) {
      // propertiesSizeLabel.textContent = variant.title;
    }
    if (optionValues.length) {
      let values = [];
      optionValues.forEach((optionValue) => {
        const selectorWrapper = optionValue.closest('[data-option-position]');
        if (selectorWrapper) {
          const optionIndex = parseInt(selectorWrapper.getAttribute('data-option-position'), 10) - 1;
          let selectedOptionValue;
          if (variant) {
            selectedOptionValue = variant.options[optionIndex];
          } else {
            const checked = this.modalContent.querySelector(`[data-single-option-selector][data-index="option${optionIndex + 1}"]:checked`);
            selectedOptionValue = checked ? checked.value : null;
          }
          if (selectedOptionValue) {
            optionValue.textContent = selectedOptionValue;
            if (values.indexOf(selectedOptionValue) === -1) values.push(selectedOptionValue);
          }
        }
      });
      const propertiesSizeInput = this.modalContent.querySelector('input#properties-size-input');
      if (propertiesSizeInput) propertiesSizeInput.value = values.join('');
    }
    
    this.updateSizeSelectionStates(formState);
  }
  updateSizeSelectionStates(formState) {
    if (!this.modalContent || !this.productForm || !this.modalContent.querySelector('[data-product-json]')) return;

    const optionInputs = this.modalContent.querySelectorAll('[data-single-option-selector]');
    const productJSON = JSON.parse(this.modalContent.querySelector('[data-product-json]').innerHTML);
    const numOptions = productJSON.options.length;

    // Read the currently checked value for each option position
    const currentSelections = [];
    for (let i = 0; i < numOptions; i++) {
      const checked = this.modalContent.querySelector(
        `[data-single-option-selector][data-index="option${i + 1}"]:checked`
      );
      currentSelections[i] = checked ? checked.value : null;
    }

    optionInputs.forEach((input) => {
      const parent = input.closest('[data-option-position]');
      if (!parent) return;

      const position = parseInt(parent.getAttribute('data-option-position'), 10) - 1;

      // Find variants matching this value combined with all other currently selected values
      const matchingVariants = productJSON.variants.filter(v => {
        if (v.options[position] !== input.value) return false;
        for (let i = 0; i < numOptions; i++) {
          if (i === position) continue;
          if (currentSelections[i] !== null && v.options[i] !== currentSelections[i]) return false;
        }
        return true;
      });

      input.classList.remove('unavailable', 'sold-out');
      input.disabled = false; // always keep clickable — CTA handles unavailable state
      const radioButton = input.closest('.radio__button');
      if (radioButton) radioButton.classList.remove('unavailable', 'sold-out');

      if (matchingVariants.length === 0 || matchingVariants.every(v => !v.available)) {
        input.classList.add('unavailable');
        if (radioButton) radioButton.classList.add('unavailable');
      }
    });
  }
   initSizeSelector() {
    if (!this.modalContent) return;
    
    const productJson = this.modalContent.querySelector('[data-product-json]');
    if (!productJson) return;

    try {
      const product = JSON.parse(productJson.innerHTML);
      
      let sizeOptionIndex = null;
      const sizeOptionNames = ['Size', 'Taille', 'size', 'taille'];
      
      product.options.forEach((option, index) => {
        if (sizeOptionNames.includes(option.name || option)) {
          sizeOptionIndex = index + 1;
        }
      });

      if (!sizeOptionIndex) {
        const allInputs = this.modalContent.querySelectorAll('.radio__buttons__variants .radio__button input');
        if (allInputs.length > 0) {
          const nonOption1Inputs = this.modalContent.querySelectorAll('.radio__buttons__variants .radio__button input[data-index]:not([data-index="option1"])');
          if (nonOption1Inputs.length > 0) {
            const firstSizeInput = nonOption1Inputs[0];
            const dataIndex = firstSizeInput.getAttribute('data-index');
            sizeOptionIndex = parseInt(dataIndex.replace('option', ''));
          }
        }
      }

      if (!sizeOptionIndex) {
        return;
      }
      const sizeOptions = this.modalContent.querySelector('.radio__buttons__variants');
      if (!sizeOptions) return;

      const sizeInputs = this.modalContent.querySelectorAll(`.radio__buttons__variants .radio__button input[data-index="option${sizeOptionIndex}"]`);
      
      if (!sizeInputs.length) return;
            
      sizeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {            
            const selectedValue = input.value;
            const optionIndex = input.getAttribute('data-index');       
            if (this.productForm && typeof this.productForm.setOptionsFromExternal === 'function') {
              const options = this.extractCurrentOptions();
              this.productForm.setOptionsFromExternal(options);
            }
            
            if (this.productForm) {
              const formState = this.productForm.getFormState();
              this.updateProductState(formState);
            }
            
            this.modalContent.dispatchEvent(new CustomEvent('size:selection:finished', {
              bubbles: true,
              detail: { selectedSize: selectedValue }
            }));
          }
        });
      });

      if (this.productForm) {
        this.modalContent.addEventListener('variant:change', (event) => {
          const formState = event.detail;
          this.updateSizeSelectorButton(formState);
        });
      }
    } catch (error) {
      console.error('CartEditModal: Error initializing size selector:', error);
    }
  }

  updateProductImage(evt) {
    const variant = evt.dataset.variant;
    
    if (variant && variant.featured_media) {
      const newImg = this.modalContent.querySelector(`[data-image-id="${variant.featured_media.id}"]`);
      const slider = this.modalContent.querySelector('[data-product-slideshow]');
      
      if (newImg && slider) {
        const newImageParent = newImg.closest('[data-slide]');
        if (newImageParent) {
          const newImagePos = Array.from(newImageParent.parentElement.children).indexOf(newImageParent);
          
          if (this.flkty) {
            this.flkty.select(newImagePos);
          }
        }
      }
    }
  }

  initSwatches() {
    const swatchElements = this.modalContent.querySelectorAll('[data-swatch]');
    if (swatchElements.length && typeof Swatch !== 'undefined') {
      swatchElements.forEach((el) => {
        try {
          new Swatch(el);
        } catch (error) {
          console.error('CartEditModal: Error initializing swatch:', error);
        }
      });
    }
    this.initColorSelector();
  }

  initColorSelector() {
    if (!this.modalContent) return;

    const productJson = this.modalContent.querySelector('[data-product-json]');
    if (!productJson) return;

    try {
      const product = JSON.parse(productJson.innerHTML);
      
      let colorOptionIndex = null;
      const colorOptionNames = ['Color', 'Colour', 'Couleur', 'color', 'colour', 'couleur'];
      
      product.options.forEach((option, index) => {
        if (colorOptionNames.includes(option.name || option)) {
          colorOptionIndex = index + 1;
        }
      });

      if (!colorOptionIndex) {
        colorOptionIndex = 1;
      }

      const colorOptions = this.modalContent.querySelectorAll(`.radio__buttons__variants input[data-index="option${colorOptionIndex}"]`);
      
      if (!colorOptions.length) return;

      colorOptions.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            const selectedColor = input.value;
            const optionIndex = input.getAttribute('data-index');
                        
            const swatchElement = input.closest('[data-swatch]');
            if (swatchElement) {
              this.modalContent.querySelectorAll('[data-swatch] .siblings__link, label[data-swatch-label]').forEach(link => {
                link.classList.remove('siblings__link--current');
              });
              
              const currentLink = swatchElement.querySelector('.siblings__link');
              if (currentLink) {
                currentLink.classList.add('siblings__link--current');
              } else {
                swatchElement.classList.add('siblings__link--current');
              }
            }
            
            if (this.productForm && typeof this.productForm.setOptionsFromExternal === 'function') {
              const options = this.extractCurrentOptions();
              this.productForm.setOptionsFromExternal(options);
            }
            
            if (this.productForm) {
              const formState = this.productForm.getFormState();
              this.updateProductState(formState);
              
              this.updateProductImageForColor(formState);
            }
            
            this.modalContent.dispatchEvent(new CustomEvent('color:selection:finished', {
              bubbles: true,
              detail: { selectedColor: selectedColor }
            }));
          }
        });
      });

      if (this.productForm) {
        this.modalContent.addEventListener('variant:change', (event) => {
          const formState = event.detail;
          this.updateColorSelectorState(formState, colorOptionIndex);
        });
      }
    } catch (error) {
      console.error('CartEditModal: Error initializing color selector:', error);
    }
  }

  updateColorSelectorState(formState, colorOptionIndex = 1) {
    if (!formState.variant) return;
    
    const colorOptions = this.modalContent.querySelectorAll(`.radio__buttons__variants input[data-index="option${colorOptionIndex}"]`);
    colorOptions.forEach((input) => {
      const variantOptionValue = formState.variant[`option${colorOptionIndex}`];
      if (input.value === variantOptionValue) {
        input.checked = true;
        
        const swatchElement = input.closest('[data-swatch]');
        if (swatchElement) {
          this.modalContent.querySelectorAll('[data-swatch] .siblings__link').forEach(link => {
            link.classList.remove('siblings__link--current');
          });
          
          const currentLink = swatchElement.querySelector('.siblings__link');
          if (currentLink) {
            currentLink.classList.add('siblings__link--current');
          }
        }
      }
    });
  }

  updateProductImageForColor(formState) {
    if (!formState.variant) return;

    const variant = formState.variant;
    
    if (variant.featured_media) {
      const mockEvent = {
        dataset: {
          variant: variant
        }
      };
      this.updateProductImage(mockEvent);
      return;
    }

    const productJson = this.modalContent.querySelector('[data-product-json]');
    if (!productJson) return;

    try {
      const product = JSON.parse(productJson.innerHTML);
      
      const sameColorVariants = product.variants.filter(v => v.option1 === variant.option1);
      let imageToShow = null;
      
      for (const colorVariant of sameColorVariants) {
        if (colorVariant.featured_media) {
          imageToShow = colorVariant.featured_media;
          break;
        }
      }

      if (imageToShow) {
        const targetImage = this.modalContent.querySelector(`[data-image-id="${imageToShow.id}"]`);
        if (targetImage) {
          const mockEvent = {
            dataset: {
              variant: { featured_media: imageToShow }
            }
          };
          this.updateProductImage(mockEvent);
        } else {
          const mainImages = this.modalContent.querySelectorAll('.product-single__photo img');
          mainImages.forEach((img) => {
            img.src = imageToShow.preview_image.src;
            img.alt = imageToShow.alt || product.title;
          });
        }
      }
    } catch (error) {
      console.error('CartEditModal: Error updating product image for color:', error);
    }
  }

  initTooltips() {
    if (typeof TooltipPopup !== 'undefined') {
      const tooltipElements = this.modalContent.querySelectorAll('[data-tooltip]');
      tooltipElements.forEach((el) => {
        try {
          new TooltipPopup(el);
        } catch (error) {
          console.error('CartEditModal: Error initializing tooltip:', error);
        }
      });
    }
  }

  initPopouts() {
    if (typeof PopoutSection !== 'undefined') {
      const popoutElements = this.modalContent.querySelectorAll('[data-popout]');
      popoutElements.forEach((el) => {
        try {
          const popoutSection = {
            container: el,
            id: 'cart-edit-modal-popout-' + Math.random().toString(36).substr(2, 9)
          };
          new PopoutSection(popoutSection);
        } catch (error) {
          console.error('CartEditModal: Error initializing popout:', error);
        }
      });
    }
  }

  initSlider() {
    const slideshow = this.modalContent.querySelector('[data-product-slideshow]');
    if (!slideshow) {
      return;
    }

    const slides = slideshow.querySelectorAll('.product__slide');
    if (slides.length <= 1) {
      if (slides.length === 1) {
        slides[0].style.opacity = '1';
        slides[0].style.visibility = 'visible';
      }
      return;
    }
    setTimeout(() => {
      this.createSimpleSlider(slideshow, slides);
    }, 200);
  }

  createSimpleSlider(slideshow, slides) {
    let currentSlide = 0;
    
    slideshow.style.position = 'relative';
    slideshow.style.overflow = 'hidden';
    slideshow.style.width = '100%';
    slideshow.style.height = '274px';
    
    const wrapper = document.createElement('div');
    wrapper.className = 'simple-slider-wrapper';
    wrapper.style.cssText = `
      display: flex;
      transition: transform 0.3s ease;
      height: 100%;
      width: ${slides.length * 100}%;
    `;
    
    slides.forEach((slide, index) => {
      slide.style.cssText = `
        width: ${100 / slides.length}%;
        height: 100%;
        flex-shrink: 0;
        opacity: 1;
        visibility: visible;
        position: relative;
      `;
    });
    
    while (slideshow.firstChild) {
      wrapper.appendChild(slideshow.firstChild);
    }
    slideshow.appendChild(wrapper);
    
    const prevButton = document.createElement('button');
    prevButton.className = 'simple-nav-prev';
    prevButton.innerHTML = '‹';
    prevButton.style.cssText = `
      position: absolute;
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid rgba(0, 0, 0, 0.1);
      width: 48px;
      height: 48px;
      border-radius: 50%;
      cursor: pointer;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: #333;
      font-weight: bold;
      transition: all 0.3s ease;
    `;
    
    const nextButton = document.createElement('button');
    nextButton.className = 'simple-nav-next';
    nextButton.innerHTML = '›';
    nextButton.style.cssText = `
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid rgba(0, 0, 0, 0.1);
      width: 48px;
      height: 48px;
      border-radius: 50%;
      cursor: pointer;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: #333;
      font-weight: bold;
      transition: all 0.3s ease;
    `;
    
    slideshow.appendChild(prevButton);
    slideshow.appendChild(nextButton);
    
    const updateSlider = () => {
      const translateX = -(currentSlide * (100 / slides.length));
      wrapper.style.transform = `translateX(${translateX}%)`;
      
      prevButton.style.opacity = currentSlide === 0 ? '0.5' : '1';
      nextButton.style.opacity = currentSlide === slides.length - 1 ? '0.5' : '1';
      prevButton.disabled = currentSlide === 0;
      nextButton.disabled = currentSlide === slides.length - 1;
    };
    
    const goToPrev = () => {
      if (currentSlide > 0) {
        currentSlide--;
        updateSlider();
      }
    };
    
    const goToNext = () => {
      if (currentSlide < slides.length - 1) {
        currentSlide++;
        updateSlider();
      }
    };
    
    prevButton.addEventListener('click', goToPrev);
    nextButton.addEventListener('click', goToNext);
    
    [prevButton, nextButton].forEach(button => {
      button.addEventListener('mouseenter', () => {
        if (!button.disabled) {
          button.style.background = 'rgba(255, 255, 255, 1)';
          button.style.transform = 'translateY(-50%) scale(1.1)';
        }
      });
      
      button.addEventListener('mouseleave', () => {
        button.style.background = 'rgba(255, 255, 255, 0.95)';
        button.style.transform = 'translateY(-50%) scale(1)';
      });
    });
    
    updateSlider();
    
    this.simpleSlider = {
      wrapper,
      prevButton,
      nextButton,
      currentSlide: () => currentSlide,
      goToPrev,
      goToNext
    };
    
  }

  forceReinitSlider() {
    this.initSlider();
  }
}

// Initialize the modal when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.cartEditModal = new CartEditModal();
});
